// TMA (Telegram Mini App) bridge.
//
// Strategy: use the legacy global `window.Telegram.WebApp` that
// `index.html` already preloads via the official telegram-web-app.js
// script tag. This is the most reliable surface — the modern
// `@telegram-apps/sdk` package wraps the same API, and we want the
// bridge to keep working even if the SDK isn't installed yet (npm
// install hasn't run, offline dev, etc).
//
// Outside Telegram (regular browser tab), `window.Telegram` is
// undefined; we return a safe light-theme + no-user default so the
// dev preview keeps working.

export type ColorScheme = 'light' | 'dark';

export interface TMAUser {
  id: number;
  firstName: string;
}

export interface TMAInitResult {
  colorScheme: ColorScheme;
  user?: TMAUser | null;
  // Raw signed initData string. The Mac verifies the HMAC against
  // BOT_TOKEN so we trust the user without a separate login. Empty
  // string when running outside Telegram (regular browser dev preview).
  initData: string;
  insideTelegram: boolean;
}

// Minimal structural typing for the global WebApp object we touch.
// We avoid `any` and only declare the fields we actually read.
interface TgWebAppUser {
  id: number;
  first_name?: string;
}

interface TgWebAppInitDataUnsafe {
  user?: TgWebAppUser;
}

interface TgWebApp {
  colorScheme?: string;
  initDataUnsafe?: TgWebAppInitDataUnsafe;
  initData?: string;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  expand?: () => void;
  ready?: () => void;
  disableVerticalSwipes?: () => void;
  requestFullscreen?: () => void;
  enableClosingConfirmation?: () => void;
  onEvent?: (event: string, cb: () => void) => void;
}

interface TgGlobal {
  WebApp?: TgWebApp;
}

declare global {
  interface Window {
    Telegram?: TgGlobal;
  }
}

function normaliseScheme(raw: string | undefined): ColorScheme {
  return raw === 'dark' ? 'dark' : 'light';
}

function applyScheme(scheme: ColorScheme): void {
  document.body.classList.toggle('theme-dark', scheme === 'dark');
  document.body.classList.toggle('theme-light', scheme === 'light');
}

export async function initTMA(): Promise<TMAInitResult> {
  const webApp: TgWebApp | undefined = window.Telegram?.WebApp;

  if (!webApp) {
    // Dev preview path — running in a normal browser, no Telegram host.
    const fallback: TMAInitResult = {
      colorScheme: 'light',
      user: null,
      initData: '',
      insideTelegram: false,
    };
    applyScheme(fallback.colorScheme);
    return fallback;
  }

  // Tell Telegram the app is ready, expand to full height, lock
  // vertical swipes so dragging the chat list doesn't close the app.
  try {
    webApp.ready?.();
    webApp.expand?.();
    webApp.disableVerticalSwipes?.();
  } catch {
    // best-effort on older hosts.
  }

  // Mirror viewportStableHeight into a CSS custom property so the
  // shell can use --tg-vh instead of 100vh (which on iOS includes
  // the URL bar and causes the visible bottom-clip we saw).
  const syncViewport = (): void => {
    const h =
      webApp.viewportStableHeight ?? webApp.viewportHeight ?? window.innerHeight;
    document.documentElement.style.setProperty("--tg-vh", `${h}px`);
  };
  syncViewport();

  const colorScheme: ColorScheme = normaliseScheme(webApp.colorScheme);
  applyScheme(colorScheme);

  const rawUser: TgWebAppUser | undefined = webApp.initDataUnsafe?.user;
  const user: TMAUser | null = rawUser
    ? {
        id: rawUser.id,
        firstName: rawUser.first_name ?? '',
      }
    : null;

  // Re-apply scheme on Telegram theme changes so the app tracks the host.
  try {
    webApp.onEvent?.('themeChanged', () => {
      applyScheme(normaliseScheme(webApp.colorScheme));
    });
    webApp.onEvent?.('viewportChanged', () => {
      if (!webApp.isExpanded) webApp.expand?.();
      syncViewport();
    });
  } catch {
    // Event subscription is best-effort.
  }

  return {
    colorScheme,
    user,
    initData: webApp.initData ?? '',
    insideTelegram: true,
  };
}
